import React, { useEffect, useState } from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import XIcon from './icons/XIcon';

interface RecipeImageGalleryProps {
    urls: string[];
    alt: string;
    language?: Language;
    variant?: 'hero' | 'compact';
    className?: string;
}

const RecipeImageGallery: React.FC<RecipeImageGalleryProps> = ({
    urls,
    alt,
    language,
    variant = 'hero',
    className = '',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    useEffect(() => {
        setActiveIndex((current) => (current >= urls.length ? Math.max(0, urls.length - 1) : current));
    }, [urls.length]);

    if (urls.length === 0) {
        return null;
    }

    const activeUrl = urls[activeIndex] ?? urls[0];
    const heroHeight = variant === 'compact' ? 'h-40' : 'h-56';
    const thumbSize = variant === 'compact' ? 'w-14 h-14' : 'w-16 h-16';

    return (
        <>
            <div className={className}>
                <button
                    type="button"
                    onClick={() => setIsLightboxOpen(true)}
                    className={`relative block w-full overflow-hidden ${variant === 'hero' ? 'rounded-t-[inherit]' : 'rounded-[1.75rem] border border-stone-100 dark:border-slate-800'}`}
                    aria-label={t('recipePhotoGalleryView')}
                >
                    <img src={activeUrl} alt={alt} className={`w-full ${heroHeight} object-cover`} />
                    {urls.length > 1 && (
                        <span className="absolute bottom-3 end-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-[10px] font-black uppercase tracking-wider">
                            {activeIndex + 1}/{urls.length}
                        </span>
                    )}
                </button>

                {urls.length > 1 && (
                    <div className={`flex gap-2 overflow-x-auto scrollbar-none ${variant === 'hero' ? 'px-4 py-3 bg-white dark:bg-slate-900' : 'mt-3'}`}>
                        {urls.map((url, index) => (
                            <button
                                key={`${url}-${index}`}
                                type="button"
                                onClick={() => setActiveIndex(index)}
                                className={`shrink-0 ${thumbSize} rounded-2xl overflow-hidden border-2 transition-all ${index === activeIndex ? 'border-brand-sage scale-[1.02]' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                aria-label={`${t('recipePhotoGalleryImage')} ${index + 1}`}
                            >
                                <img src={url} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {isLightboxOpen && (
                <div
                    className="fixed inset-0 z-[800] bg-black/90 backdrop-blur-sm flex flex-col"
                    onClick={() => setIsLightboxOpen(false)}
                >
                    <div className="flex items-center justify-between px-4 py-4 text-white">
                        <span className="text-sm font-bold">
                            {urls.length > 1 ? `${activeIndex + 1} / ${urls.length}` : alt}
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsLightboxOpen(false)}
                            className="p-2 rounded-full hover:bg-white/10 transition-colors"
                            aria-label={t('close')}
                        >
                            <XIcon className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="flex-1 flex items-center justify-center px-4 pb-6" onClick={(event) => event.stopPropagation()}>
                        <img
                            src={activeUrl}
                            alt={alt}
                            className="max-w-full max-h-[75vh] object-contain rounded-2xl"
                        />
                    </div>
                    {urls.length > 1 && (
                        <div className="px-4 pb-6 flex gap-2 overflow-x-auto scrollbar-none justify-center">
                            {urls.map((url, index) => (
                                <button
                                    key={`lightbox-${url}-${index}`}
                                    type="button"
                                    onClick={() => setActiveIndex(index)}
                                    className={`shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 ${index === activeIndex ? 'border-white' : 'border-white/20 opacity-60'}`}
                                >
                                    <img src={url} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default RecipeImageGallery;
